import { groups, allFaqs } from "@/lib/faq-data";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { LeadSection } from "@/components/LeadSection";
import { SiteFooter } from "@/components/SiteFooter";

const TITLE = "FAQ: Shopify Shop-Erstellung, Anpassung & Conversion";
const DESCRIPTION =
  "Antworten zu Shop-Aufbau, Theme-Anpassung (110 €/Std.), Conversion-Optimierung (ab 3.990 €), Ablauf, Messung und Zusammenarbeit mit einem Shopify Freelancer.";


export const Route = createFileRoute("/faq")({
  component: FaqPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://shopify-freelancer.net/faq" },
      { property: "og:type", content: "article" },
    ],
    links: [
      { rel: "canonical", href: "https://shopify-freelancer.net/faq" },
      { rel: "alternate", hrefLang: "de", href: "https://shopify-freelancer.net/faq" },
      { rel: "alternate", hrefLang: "en", href: "https://shopify-freelancer.net/en/faq" },
      { rel: "alternate", hrefLang: "x-default", href: "https://shopify-freelancer.net/faq" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: allFaqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
});

function FaqPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main>
        <section className="veil border-b border-border/60 py-14 md:py-20">
          <div className="mx-auto max-w-3xl px-5">
            <h1 className="text-4xl font-bold md:text-5xl">
              Fragen & Antworten zu Shopify Shop-Erstellung, Anpassung und Conversion
            </h1>
            <p className="mt-5 leading-relaxed text-muted-foreground">
              {allFaqs.length} Fragen, die Shop-Betreiber mir am häufigsten stellen,
              von den ersten Schritten bis zur Messung, ausführlich beantwortet, ohne
              Marketing-Nebel. Wenn Ihre Frage fehlt, schreiben Sie mir; sie landet
              danach hier.
            </p>
            <nav className="mt-8 flex flex-wrap gap-2">
              {groups.map((g) => (
                <a
                  key={g.id}
                  href={`#${g.id}`}
                  className="rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-foreground"
                >
                  {g.title}
                </a>
              ))}
            </nav>
          </div>
        </section>

        {groups.map((g) => (
          <section key={g.id} id={g.id} className="border-b border-border/60 py-14 scroll-mt-20">
            <div className="mx-auto max-w-3xl px-5">
              <h2 className="text-2xl font-bold md:text-3xl">{g.title}</h2>
              <div className="mt-6 divide-y divide-border overflow-hidden rounded-lg border border-border">
                {g.items.map((f) => (
                  <article key={f.q} className="bg-card p-6">
                    <h3 className="text-base font-semibold">{f.q}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {f.a}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        ))}

        <section className="py-14">
          <div className="mx-auto max-w-3xl px-5 text-center">
            <p className="text-muted-foreground">
              Rechnen Sie zuerst nach, ob es sich lohnt:{" "}
              <Link to="/roi-rechner" className="text-primary underline underline-offset-4">
                zum Conversion-Rechner
              </Link>{" "}
              . Oder{" "}
              <Link to="/kontakt" className="text-primary underline underline-offset-4">
                schreiben Sie mir Ihre Frage
              </Link>
              .
            </p>
          </div>
        </section>
        <LeadSection source="faq" />
      </main>
      <SiteFooter />
    </div>
  );
}
